/// <amd-module name="Release/Landing/HubContent/Releases/ActiveReleasesActionCreator" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssFeatureService } from "VSS/Platform/Feature";
import { IVssContributionService } from "VSS/Platform/Contribution";

import { IReleaseService } from "Release/Client/Service/Release";
import { Release, EnvironmentStatus, DeploymentStatus, DeploymentOperationStatus, ReleaseEnvironment, ReleaseStatus, ReleaseEnvironmentExpands } from "Release/Client/Contracts/Release";
import { IReleasesResult, IReleasesHubActiveReleasesData } from "Release/Client/Contracts/Extended";
import { ActionCreatorBase, Action } from "DistributedTask/Flux/Actions";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { ActiveReleasesActions, IActiveReleasesPayload } from "./ActiveReleasesActions";
import { RmServiceInstanceId } from "Constants";
import { IReleaseHubServiceDataService } from "Release/Landing/PageData";
import { IReleasesFilterState } from "Release/Landing/HubContent/Filter/FilterTypes";
import { MessageActions } from "Release/Landing/HubMessageBar/MessageActions";
import { MessageBarKeys } from "Release/Landing/Constants";
import { IActiveReleasesData } from "DataProviderContracts";
import { isSignalRConnected } from "Release/Landing/SignalR/LandingSignalRHub";

const activeReleasesDataProviderId: string = "ms.vss-releaseManagement-web.active-releases-data-provider-new";
const releasesListDataProviderId: string = "ms.vss-releaseManagement-web.releases-list-data-provider";

export class ActiveReleasesActionCreator extends ActionCreatorBase {
    public static getKey(): string {
        return "release-landing-active-releases-actioncreator";
    }

    public initialize(): void {
        this._actions = ActionsHubManager.GetActionsHub<ActiveReleasesActions>(ActiveReleasesActions);
        this._messageActions = ActionsHubManager.GetActionsHub<MessageActions>(MessageActions);
    }

    public fetchReleases(pageContext: IVssPageContext, definitionId: number, definitionfolderPath: string, selectedFolderPath: string): void {
        this._currentQueriedDefinitionId = definitionId;

        const raiseAction = (releases: Release[], continuationToken: number, envReleaseMap: { [envId: number]: number } | undefined | null) => {
            if (definitionId === this._currentQueriedDefinitionId) {
                let payload: IActiveReleasesPayload = {
                    definitionId: definitionId,
                    folderPath: definitionfolderPath === "" ? selectedFolderPath : definitionfolderPath,
                    releases: releases,
                    continuationToken: continuationToken,
                    definitionEnvironmentCurrentReleaseMap: envReleaseMap,
                    isLoading: false
                };

                this._actions.releasesUpdated.invoke(payload);
            }
        };

        const rmDataService = pageContext.getService<IReleaseHubServiceDataService>("IReleaseHubServiceDataService");
        let releasesFromData = rmDataService.getReleases(definitionId, selectedFolderPath);
        if (releasesFromData) {
            raiseAction(releasesFromData.releases, releasesFromData.continuationToken, releasesFromData.definitionEnvironmentCurrentReleaseMap);
        }
        else {
            rmDataService.getReleasesAsync(definitionId, selectedFolderPath)
                .then((releasesFromData: IActiveReleasesData | undefined) => {
                    if (releasesFromData) {
                        raiseAction(releasesFromData.releases, releasesFromData.continuationToken, releasesFromData.definitionEnvironmentCurrentReleaseMap);
                        return;
                    }

                    this._listReleases(pageContext, definitionId, true, selectedFolderPath)
                        .then((result: IActiveReleasesData | null | undefined) => {
                            if (!result) {
                                raiseAction([], 0, null);
                            }
                            else {
                                raiseAction(result.releases, result.continuationToken || 0, result.definitionEnvironmentCurrentReleaseMap);
                            }
                        });
                },
                    (error) => {
                        this._handleError(error);
                        raiseAction([], 0, null);
                    });
        }
    }

    public fetchActiveReleases(pageContext: IVssPageContext, definitionId: number, definitionfolderPath: string, selectedFolderPath: string): void {
        this._currentQueriedDefinitionId = definitionId;

        const raiseAction = (releases: Release[], continuationToken: number, envReleaseMap: { [envId: number]: number } | undefined | null) => {

            if (definitionId === this._currentQueriedDefinitionId) {
                let payload: IActiveReleasesPayload = {
                    definitionId: definitionId,
                    folderPath: definitionfolderPath === "" ? selectedFolderPath : definitionfolderPath,
                    releases: releases,
                    definitionEnvironmentCurrentReleaseMap: envReleaseMap,
                    continuationToken: continuationToken,
                    isLoading: false
                };

                this._actions.releasesUpdated.invoke(payload);
            }
        };

        const rmDataService = pageContext.getService<IReleaseHubServiceDataService>("IReleaseHubServiceDataService");
        let releasesFromData = rmDataService.getReleases(definitionId);
        if (releasesFromData) {
            raiseAction(releasesFromData.releases, releasesFromData.continuationToken, releasesFromData.definitionEnvironmentCurrentReleaseMap);
        }
        else {
            rmDataService.getReleasesAsync(definitionId).then((releasesFromData: IActiveReleasesData) => {
                if (releasesFromData) {
                    raiseAction(releasesFromData.releases, releasesFromData.continuationToken, releasesFromData.definitionEnvironmentCurrentReleaseMap);
                    return;
                }

                this._listReleases(pageContext, definitionId, false)
                    .then((result: IActiveReleasesData | null | undefined) => {
                        if (!result) {
                            raiseAction([], 0, null);
                        }
                        else {
                            raiseAction(result.releases, result.continuationToken || 0, result.definitionEnvironmentCurrentReleaseMap);
                        }
                    });
            },
                (error) => {
                    this._handleError(error);
                    raiseAction([], 0, null);
                });
        }
    }

    private _listReleases(pageContext: IVssPageContext, definitionId: number, fetchAllReleases: boolean, selectedFolderPath: string = "", continuationToken: number = 0): Promise<IActiveReleasesData | null | undefined> {
        return this._listReleasesWithFilters(pageContext, definitionId, fetchAllReleases, selectedFolderPath, continuationToken, "", ReleaseStatus.Undefined, false, "", [], "");
    }

    private _listReleasesWithFilters(
        pageContext: IVssPageContext,
        definitionId: number,
        fetchAllReleases: boolean,
        selectedFolderPath: string,
        continuationToken: number,
        searchQuery: string,
        releaseStatus: ReleaseStatus,
        isDeleted: boolean,
        branch: string,
        tags: string[],
        createdBy: string
    ): Promise<IActiveReleasesData | null | undefined> {
        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        let additionalProperties = {
            definitionId: !selectedFolderPath ? definitionId : 0,
            continuationToken: continuationToken,
            fetchAllReleases: fetchAllReleases,
            folderPath: selectedFolderPath,
            searchQuery: searchQuery,
            state: releaseStatus || ReleaseStatus.Undefined,
            deleted: isDeleted || false,
            branch: branch || "",
            createdBy: createdBy || "",
            tags: tags || []
        };

        return contributionService.getDataAsync<IActiveReleasesData>(
            releasesListDataProviderId,
            additionalProperties,
            true,
            RmServiceInstanceId).then((result: IActiveReleasesData | null | undefined) => {
                return result;
            },
                (error: Error) => {
                    this._messageActions.setMessage.invoke({ key: MessageBarKeys.HubError, value: error.message ? error.message : error });
                    throw error;
                }
            );
    }

    public searchReleases(pageContext: IVssPageContext, definitionId: number, definitionfolderPath: string, selectedFolderPath: string, filterState: IReleasesFilterState): void {
        this._currentQueriedDefinitionId = definitionId;

        // ToDo: implementation of throttling
        const raiseAction = (releases: Release[], continuationToken: number, environmentReleaseMap?: { [envId: number]: number } | null) => {
            if (definitionId === this._currentQueriedDefinitionId) {
                let payload: IActiveReleasesPayload = {
                    definitionId: definitionId,
                    folderPath: definitionfolderPath,
                    releases: releases,
                    filterState: filterState,
                    definitionEnvironmentCurrentReleaseMap: environmentReleaseMap,
                    continuationToken: continuationToken,
                    isLoading: false
                };

                this._actions.releasesUpdated.invoke(payload);
            }
        };

        if (filterState.currentlyDeployed) {
            this._listReleases(pageContext, definitionId, false, selectedFolderPath)
                .then((result: IActiveReleasesData) => {
                    raiseAction(result.releases, result.continuationToken || 0);
                },
                    (error) => {
                        this._handleError(error);
                    });
        }
        else {
            // First check whether there is data available in the data provider
            const rmDataService = pageContext.getService<IReleaseHubServiceDataService>("IReleaseHubServiceDataService");
            let releasesFromData = rmDataService.getReleases(definitionId);
            if (releasesFromData) {
                raiseAction(releasesFromData.releases, releasesFromData.continuationToken, releasesFromData.definitionEnvironmentCurrentReleaseMap);
            }
            else {
                rmDataService.getReleasesAsync(definitionId).then((releasesFromData: IActiveReleasesData) => {
                    if (releasesFromData) {
                        raiseAction(releasesFromData.releases, releasesFromData.continuationToken, releasesFromData.definitionEnvironmentCurrentReleaseMap);
                        return;
                    }
                    else {
                        this._listReleasesWithFilters(pageContext, !selectedFolderPath ? definitionId : 0, true, selectedFolderPath, 0, filterState.searchText, filterState.status, filterState.isDeleted, filterState.branch || "", filterState.tags, filterState.createdBy)
                            .then((result: IActiveReleasesData) => {
                                raiseAction(result.releases, result.continuationToken || 0);
                            },
                                (error) => {
                                    this._handleError(error);
                                    raiseAction([], 0);
                                });
                    }
                });
            }
        }
    }

    public fetchMoreReleases(pageContext: IVssPageContext, definitionId: number, definitionfolderPath: string, selectedFolderPath: string, filterState: IReleasesFilterState, continuationToken: number = 0): void {
        let { searchText, status, branch, isDeleted, tags, createdBy } = filterState;
        this._currentQueriedDefinitionId = definitionId;

        // 0, negative, null or undefined
        if (!continuationToken || continuationToken <= 0) {
            return;
        }

        let promise: Promise<IActiveReleasesData | null | undefined>;
        if (filterState.currentlyDeployed) {
            promise = this._listReleases(pageContext, definitionId, false, selectedFolderPath, continuationToken);
        }
        else {
            promise = this._listReleasesWithFilters(pageContext, definitionId, true, selectedFolderPath, continuationToken, searchText, status, isDeleted, branch || "", tags, createdBy);
        }

        const raiseAction = (releases: Release[], continuationToken: number) => {
            if (definitionId === this._currentQueriedDefinitionId) {
                let payload: IActiveReleasesPayload = {
                    definitionId: definitionId,
                    folderPath: definitionfolderPath === "" ? selectedFolderPath : definitionfolderPath,
                    releases: releases,
                    definitionEnvironmentCurrentReleaseMap: null,
                    continuationToken: continuationToken,
                    isLoading: false
                };

                this._actions.releasesUpdatedWithMore.invoke(payload);
            }
        };

        promise
            .then((releasesResult: IActiveReleasesData | undefined) => {

                const releases = releasesResult ? releasesResult.releases : [];
                const nextContinuationToken = releasesResult ? releasesResult.continuationToken : 0;
                if (definitionId === this._currentQueriedDefinitionId) {
                    raiseAction(releases, nextContinuationToken);
                }
            },
                (error) => {
                    this._handleError(error);
                    raiseAction([], continuationToken);
                });
    }

    public updateReleaseEnvironmentStatus(
        definitionId: number,
        releaseId: number,
        releaseEnvironmentId: number,
        environmentStatus: EnvironmentStatus,
        deploymentStatus: DeploymentStatus,
        operationStatus: DeploymentOperationStatus
    ): void {
        this._actions.updateReleaseEnvironmentStatus.invoke({
            definitionId: definitionId,
            releaseId: releaseId,
            environmentId: releaseEnvironmentId,
            deploymentStatus: deploymentStatus,
            operationStatus: operationStatus,
            environmentStatus: environmentStatus
        });
    }

    public getCreatedRelease(pageContext: IVssPageContext, releaseId: number): void {
        pageContext.getService<IReleaseService>("IReleaseService").getRelease(releaseId)
            .then((release: Release) => {
                this._actions.releaseCreated.invoke({ release: release });
            });
    }

    public handleCreatedRelease(pageContext: IVssPageContext, release: Release) {
        if (!isSignalRConnected()) {
            this._actions.releaseCreated.invoke({ release: release });
        }
    }

    public fetchAndUpdateEnvironment(pageContext: IVssPageContext, definitionId: number, releaseId: number, releaseEnvironmentId: number, updateOnlyNotDeployed: boolean): void {
        pageContext.getService<IReleaseService>("IReleaseService").getReleaseEnvironment(releaseId, releaseEnvironmentId, ReleaseEnvironmentExpands.None)
            .then((releaseEnvironment: ReleaseEnvironment) => {
                this._actions.handleFetchedReleaseEnvironment.invoke({ definitionId: definitionId, environment: releaseEnvironment, updateOnlyNotDeployed: updateOnlyNotDeployed });
            });
    }

    private _handleError(error: any): void {
        let errorMessage: string = error ? error.message || error : "";
        this._messageActions.setMessage.invoke({ key: MessageBarKeys.HubError, value: errorMessage });
    }

    private _currentQueriedDefinitionId: number;
    private _actions: ActiveReleasesActions;
    private _messageActions: MessageActions;
}